import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Grid,
  Autocomplete,
  FormHelperText,
  CircularProgress
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import { Add, Edit, Delete, Close, CloudUpload } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { GlobalStyles, useTheme, useMediaQuery } from '@mui/material';
import usePermissions from "../../../hooks/usePermissions";
import { fetchPushNotifications, createPushNotification, updatePushNotification, deletePushNotification, sendPushNotification, fetchPushNotificationById } from "../../../Redux/Reducers/PushNotificationsReducer";
import { fetchCustomers } from "../../../Redux/Reducers/CustomersReducer";
import { formatUTCDate, formatUTCDateForDataGrid } from "../../../utils/dateUtils";
import { Link } from "react-router-dom";


const MODULE_KEY = "push-notifications";

const USER_TYPES = [
    { label: "All Users", value: "ALL" },
    { label: "Free Users", value: "FREE_USERS" },
    { label: "Premium Users", value: "PREMIUM_USERS" },
    { label: "Specific Users (by ID/email upload)", value: "SPECIFIC_USERS" },
];

const ACTION_TYPES =  [
    { label: "Open App", value: "OPEN_APP" },
    { label: "Open Specific Screen", value: "OPEN_SCREEN" }
];

const SCREENS =  [
    { label: "HOME", value: "HOME" },
    { label: "PLAYER", value: "PLAYER" },
    { label: "ALBUM", value: "ALBUM" },
    { label: "CATEGORY", value: "CATEGORY" },
    { label: "SEARCH", value: "SEARCH" },
];

function PushNotificationManagement() {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { hasAction } = usePermissions();

    const { notifications, loading, pagination } = useSelector((state) => state.pushNotifications || {});

    const { customers = [] } = useSelector((state) => state.customers);

    const [ modalOpen, setModalOpen ] = useState(false);
    const [ editingPushNotification, setEditingPushNotification] = useState(null);
    const [ deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [ selectedId, setSelectedId ] = useState(null);
    const [ searchText, setSearchText ] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sending, setSending] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [actionType, setActionType] = useState(null);

    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);

    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [viewNotification, setViewNotification] = useState(null);

    // For Table Thead Width
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const isLarge = useMediaQuery(theme.breakpoints.up('lg'));

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const {
        control,
        watch,
        handleSubmit,
        reset,
        setValue,
        setError,
        formState: { errors }
    } = useForm({
        defaultValues: {
            title: "",
            message: "",
            target_type: "",
            recipient_ids: [],
            action_type: "",
            internal_screen: "",
            send_immediately: true,
        }
    });

    const selectedTargetType = watch("target_type");
    const selectActionType = watch("action_type");

    useEffect(() => {
        dispatch(fetchPushNotifications({
                page: paginationModel.page + 1,
                limit: paginationModel.pageSize,
                search: searchText,
            }
        ));
    }, [
        dispatch,
        paginationModel.page,
        paginationModel.pageSize,
        searchText
    ]);

    // Fetch customers when type changes
    useEffect(() => {
        if (selectedTargetType === "SPECIFIC_USERS") {
            dispatch(fetchCustomers({ fetchType: "dropdown", limit: 100000 }));
        }
    }, [selectedTargetType, dispatch]);

    const notificationList = useMemo(() => {
        if (!notifications) return [];
        if (Array.isArray(notifications)) return notifications;
        if (notifications.data && Array.isArray(notifications.data)) return notifications.data;
        return [];
    }, [notifications]);

    const handleViewNotification = async (row) => {
        const res = await dispatch(fetchPushNotificationById(row.id)).unwrap();
        setViewNotification(res);
        setViewDialogOpen(true);
    };

    const handleOpenModal = (row = null) => {
        if (row) {
            setEditingPushNotification(row);
            reset({
                title: row.title || "",
                message: row.message || "",
                target_type: row.targetType || "",
                recipient_ids: Array.isArray(row.recipientIds) ? row.recipientIds : [],
                action_type: row.action_type || "",
                internal_screen: row.internal_screen || "",
                send_immediately: row.send_immediately ?? true,
            });
        } else {
            setEditingPushNotification(null);
            reset({
                title: "",
                message: "",
                target_type: "",
                recipient_ids: [],
                action_type: "",
                internal_screen: "",
                send_immediately: true,
            });
        }
        setModalOpen(true);
    };


    const handleCloseModal = () => {
        reset();
        setEditingPushNotification(null);
        setModalOpen(false);
    };

    const onSubmit = async (data) => {
        if (submitting) return;
        try {     
            setSubmitting(true);

            if (data.file && data.file.length > 0) {
                data.file.forEach((file) => {
                    data.attachments = file;
                });
            }

            if (editingPushNotification) {
                await dispatch(
                    updatePushNotification({ id: editingPushNotification.id, formData: data })
                ).unwrap();

                toast.success(t('notifications.push_notifications.messages.update_success', 'Push notifications updated successfully'));
            } else {
                await dispatch(createPushNotification(data)).unwrap();
                
                toast.success(t('notifications.push_notifications.messages.create_success', 'Push notifications created successfully'));
            }
            handleCloseModal();

            dispatch(fetchPushNotifications({
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                    search: searchText,
                }
            ));
        } catch (err) {

            if (!err?.errors) {
                toast.error(err?.message || "Something went wrong");
            }

            // Handle field errors
            if (err?.errors) {
                Object.keys(err.errors).forEach((key) => {
                    setError(key, {
                        type: "server",
                        message: err.errors[key][0],
                    });
                });                
            }
        } finally {            
            setSubmitting(false);
            setActionType(null);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        try {
            await dispatch(deletePushNotification({id: selectedId})).unwrap();
            toast.success(t('notifications.push_notifications.messages.delete_success', 'Push notification deleted successfully'));

            dispatch(fetchPushNotifications({
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                    search: searchText,
                }
            ));
        } catch {
            toast.error(t('delete_failed', 'Delete failed'));
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
        }
    };

    const handleSendConfirm = async () => {
        setSending(true);
        try {
            await dispatch(
                sendPushNotification({ id : selectedNotification.id })
            ).unwrap();

            toast.success(
                t(
                    'notifications.push_notifications.messages.send_success',
                    'Push notification sent successfully'
                )
            );

            dispatch(
                fetchPushNotifications({
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                    search: searchText,
                })
            );
        } catch (err) {
            toast.error(
                err?.message ||
                t(
                    'notifications.push_notifications.messages.send_failed',
                    'Failed to send push notification'
                )
            );
        } finally {
            setSendDialogOpen(false);
            setSelectedNotification(null);
        }
    };

    const columns = 
    [
        { 
            field: "sno", 
            headerName: t('sno', 'S.No'), 
            width: 60, 
            sortable: false 
        },
        { 
            field: "title", 
            headerName: t('notifications.push_notifications.fields.title', 'Title'), 
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
        },
        { 
            field: "message", 
            headerName: t('notifications.push_notifications.fields.message', 'Message'), 
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
        },
        // { 
        //     field: "isSent", 
        //     headerName: t('notifications.push_notifications.fields.is_sent', 'Notification Sent'), 
        //     ...(isMobile && { width: 150 }),
        //     ...(isLarge && { flex: 1 }),
        //     renderCell: (params) => {
        //         return (params.row.isSent === true ? "Yes" : "No");
        //     }
        // },
        { 
            field: "sentAt", 
            headerName: t('notifications.push_notifications.fields.sent_at', 'Sent At'), 
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            renderCell: (params) => {
                return formatUTCDate(params.row.sentAt);
            }
        },
        { 
            field: "createdAt", 
            headerName: t('notifications.push_notifications.fields.createdAt', 'Created At'), 
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            valueFormatter: (value) => {
                return formatUTCDateForDataGrid(value);
            }
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 180,
            sortable: false,
            renderCell: (params) => {
            return (
                <Box sx={{ display: 'flex', gap: '6px' }}>
                    <IconButton
                        size="small"
                        component={Link}
                        onClick={() => handleViewNotification(params.row)}
                        sx={{
                            backgroundColor: "rgba(227,214,245,0.2) !important",
                            width: "36px",
                            height: "36px",
                            border: "1px solid rgba(227,214,245,0.1)",
                            padding: "0"
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="13" viewBox="0 0 18 13" fill="none">
                            <path d="M17.415 4.66071C15.5893 1.74214 12.4457 0 9 0C5.55429 0 2.41071 1.74214 0.585 4.66071L0.437143 4.89857C0.154286 5.36143 0 5.88857 0 6.42857C0 6.96857 0.154286 7.49571 0.437143 7.95857L0.585 8.19643C2.41071 11.115 5.55429 12.8571 9 12.8571C12.4457 12.8571 15.5893 11.115 17.415 8.19643L17.5629 7.95857C17.8457 7.49571 18 6.96857 18 6.42857C18 5.88857 17.8457 5.36143 17.5629 4.89857L17.415 4.66071ZM16.47 7.27714L16.3221 7.515C14.7343 10.0543 11.9957 11.5714 9 11.5714C6.00429 11.5714 3.26571 10.0543 1.67786 7.515L1.53 7.27714C1.36929 7.02 1.28571 6.73071 1.28571 6.42857C1.28571 6.12643 1.36929 5.83714 1.53 5.58L1.67786 5.34214C3.26571 2.80286 6.00429 1.28571 9 1.28571C11.9957 1.28571 14.7343 2.80286 16.3221 5.34214L16.47 5.58C16.6307 5.83714 16.7143 6.12643 16.7143 6.42857C16.7143 6.73071 16.6307 7.02 16.47 7.27714Z" fill="#ffffff" />
                            <path d="M8.99972 2.57129C6.87186 2.57129 5.14258 4.30057 5.14258 6.42843C5.14258 8.55629 6.87186 10.2856 8.99972 10.2856C11.1276 10.2856 12.8569 8.55629 12.8569 6.42843C12.8569 4.30057 11.1276 2.57129 8.99972 2.57129ZM8.99972 8.99986C7.57901 8.99986 6.42829 7.84915 6.42829 6.42843C6.42829 5.00772 7.57901 3.857 8.99972 3.857C10.4204 3.857 11.5711 5.00772 11.5711 6.42843C11.5711 7.84915 10.4204 8.99986 8.99972 8.99986Z" fill="#ffffff" />
                        </svg>
                    </IconButton>
                    {params.row.isSent === false && (
                        <IconButton
                            size="small"
                            component={Link}
                            onClick={() => handleOpenModal(params.row)}
                            sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                            >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M15.531 3.84226C13.9473 5.42594 10.2302 9.14231 9.3237 10.0459C9.12779 10.2411 8.89463 10.3952 8.64435 10.4958C7.91732 10.7975 6.14371 11.4932 5.62228 11.5283C5.31165 11.5491 5.00699 11.4344 4.7865 11.2139C4.566 10.9934 4.45129 10.6887 4.47215 10.3781C4.50716 9.85668 5.2029 8.08453 5.49937 7.36048C5.60291 7.10348 5.7571 6.87032 5.95301 6.67516L12.1581 0.469295C12.4582 0.169095 12.8665 0 13.291 0C13.7156 0 14.1239 0.169095 14.424 0.469295L15.531 1.57623C15.8312 1.87643 16.0003 2.28465 16.0003 2.70925C16.0003 3.13384 15.8312 3.54206 15.531 3.84226ZM5.59174 10.4087C6.10125 10.3446 7.59031 9.72334 8.21603 9.46336C8.21826 9.46262 8.2205 9.46187 8.22199 9.46113C8.33894 9.4142 8.44471 9.34418 8.53336 9.25553L8.5341 9.25479C9.44214 8.35121 13.1577 4.63559 14.7406 3.0519C14.8315 2.96103 14.8829 2.83811 14.8829 2.70925C14.8829 2.58038 14.8315 2.45746 14.7406 2.36659L13.6337 1.25965C13.5428 1.16877 13.4199 1.11737 13.291 1.11737C13.1622 1.11737 13.0393 1.16877 12.9484 1.25965L6.74261 7.46551C6.65322 7.55415 6.5832 7.66068 6.53627 7.77763L6.53478 7.78061C6.27779 8.40782 5.6558 9.89839 5.59174 10.4087Z" fill="white"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M14.0542 4.52797C14.2717 4.74623 14.2717 5.10006 14.0542 5.31832C13.8359 5.53658 13.4821 5.53658 13.2638 5.31832L10.6813 2.73571C10.463 2.51745 10.463 2.16362 10.6813 1.94536C10.8995 1.72785 11.2534 1.72785 11.4716 1.94536L14.0542 4.52797Z" fill="white"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M9.49485 9.08712C9.71311 9.30464 9.71311 9.65922 9.49485 9.87673C9.27734 10.095 8.92277 10.095 8.70525 9.87673L6.12268 7.29412C5.90442 7.07661 5.90442 6.72203 6.12268 6.50451C6.34019 6.28626 6.69476 6.28626 6.91227 6.50451L9.49485 9.08712Z" fill="white"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M5.40054 1.47412C5.70893 1.47412 5.95922 1.72441 5.95922 2.03281C5.95922 2.3412 5.70893 2.59149 5.40054 2.59149H2.79339C1.86747 2.59149 1.11735 3.34162 1.11735 4.26754V13.2065C1.11735 13.6512 1.2939 14.0773 1.60824 14.3916C1.92259 14.706 2.34868 14.8825 2.79339 14.8825H12.4771C12.9218 14.8825 13.3479 14.706 13.6623 14.3916C13.9766 14.0773 14.1532 13.6512 14.1532 13.2065V10.5993C14.1532 10.2909 14.4034 10.0406 14.7118 10.0406C15.0202 10.0406 15.2705 10.2909 15.2705 10.5993V13.2065C15.2705 13.9477 14.9763 14.6576 14.4526 15.182C13.9282 15.7057 13.2183 15.9999 12.4771 15.9999H2.79339C2.05221 15.9999 1.34231 15.7057 0.817903 15.182C0.294237 14.6576 0 13.9477 0 13.2065V4.26754C0 2.72483 1.25069 1.47412 2.79339 1.47412H5.40054Z" fill="white"/>
                            </svg>
                        </IconButton>
                    )}

                    {params.row.isSent === false && (
                        <IconButton
                            size="small"
                            component={Link}
                            onClick={() => {
                                setSelectedNotification(params.row);
                                setSendDialogOpen(true);
                            }}
                            sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                            >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M14.9672 0.0449725C15.2493 -0.0557718 15.5601 0.0161884 15.7731 0.226312C15.9861 0.436436 16.0552 0.750183 15.9545 1.03227L10.8311 15.3351C10.6872 15.7352 10.3101 16 9.887 16C9.47828 16 9.10985 15.7525 8.9573 15.3754L7.10943 10.8275C6.9799 10.5109 7.03747 10.1482 7.2591 9.88913L9.9791 6.6538C10.1259 6.47822 10.1144 6.22204 9.9532 6.06085C9.79201 5.89966 9.53297 5.88814 9.36027 6.03494L6.12505 8.74928C5.86312 8.96804 5.50333 9.02561 5.18672 8.89896L0.624593 7.04239C0.247535 6.88983 0 6.5214 0 6.11266C0 5.68953 0.264805 5.31246 0.66489 5.16854L14.9672 0.0449725Z" fill="white"/>
                            </svg>
                        </IconButton>
                    )}
                    {params.row.isSent === false && (
                    <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(params.row.id)}
                        sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                        >
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
                            <path d="M7.21951 1.17073C6.4558 1.17073 5.80457 1.65856 5.56322 2.34141C5.45548 2.64622 5.12105 2.80599 4.81623 2.69825C4.51142 2.59051 4.35166 2.25608 4.4594 1.95127C4.86091 0.81529 5.94427 0 7.21951 0C8.49483 0 9.57815 0.81529 9.97971 1.95127C10.0874 2.25608 9.92765 2.59051 9.62287 2.69825C9.31801 2.80599 8.98357 2.64622 8.87586 2.34141C8.63454 1.65856 7.98322 1.17073 7.21951 1.17073Z" fill="white"/>
                            <path d="M0 3.70744C0 3.38415 0.26208 3.12207 0.585366 3.12207H13.8537C14.177 3.12207 14.4391 3.38415 14.4391 3.70744C14.4391 4.03072 14.177 4.2928 13.8537 4.2928H0.585366C0.26208 4.2928 0 4.03072 0 3.70744Z" fill="white"/>
                            <path d="M2.47023 5.61969C2.44873 5.29712 2.1698 5.05306 1.84723 5.07456C1.52466 5.09606 1.28059 5.37499 1.3021 5.69757L1.66382 11.1233C1.73054 12.1245 1.78444 12.9332 1.91086 13.5678C2.04229 14.2275 2.26584 14.7786 2.72758 15.2106C3.1893 15.6426 3.75403 15.8289 4.42107 15.9162C5.06266 16.0001 5.87318 16.0001 6.87657 16.0001H7.56246C8.56586 16.0001 9.37632 16.0001 10.018 15.9162C10.685 15.8289 11.2497 15.6426 11.7115 15.2106C12.1732 14.7786 12.3967 14.2275 12.5282 13.5678C12.6545 12.9332 12.7085 12.1245 12.7752 11.1233L13.1369 5.69757C13.1584 5.37499 12.9143 5.09606 12.5918 5.07456C12.2692 5.05306 11.9902 5.29712 11.9688 5.61969L11.6098 11.0043C11.5397 12.0564 11.4896 12.7883 11.38 13.339C11.2735 13.8733 11.125 14.156 10.9116 14.3557C10.6982 14.5553 10.4062 14.6847 9.86607 14.7554C9.30927 14.8282 8.57562 14.8294 7.52133 14.8294H6.9177C5.86342 14.8294 5.12972 14.8282 4.57292 14.7554C4.03282 14.6847 3.7408 14.5553 3.5274 14.3557C3.314 14.156 3.16545 13.8733 3.05903 13.339C2.94932 12.7883 2.89935 12.0564 2.82922 11.0043L2.47023 5.61969Z" fill="white"/>
                            <path d="M5.20978 7.02736C5.53147 6.9952 5.81828 7.2299 5.85051 7.55161L6.24076 11.4541C6.27291 11.7757 6.03822 12.0626 5.71651 12.0948C5.39484 12.1269 5.10797 11.8922 5.07581 11.5705L4.68557 7.66806C4.65339 7.34642 4.8881 7.05952 5.20978 7.02736Z" fill="white"/>
                            <path d="M9.75346 7.66806C9.78561 7.34642 9.55092 7.05952 9.22921 7.02736C8.90757 6.9952 8.62066 7.2299 8.5885 7.55161L8.19826 11.4541C8.1661 11.7757 8.4008 12.0626 8.72251 12.0948C9.04415 12.1269 9.33098 11.8922 9.36321 11.5705L9.75346 7.66806Z" fill="white"/>
                        </svg>
                    </IconButton>
                    )}
                </Box>
            )}
        }
    ];
    
    const rows = useMemo(() => {
        return notificationList.map((record, index) => ({
            id: record.id,
            sno: paginationModel.page * paginationModel.pageSize + index + 1,
            title: record.title || "",
            message: record.message || "",
            isSent: record.isSent ?? false,
            sentAt: record.sentAt || null,
            targetType: record.targetType || "",
            action_type: record.actionType || "",
            internal_screen: record.internalScreen || "",
            recipientIds: record.recipientIds || "",            
            createdAt: record.createdAt || ""
        }))
        .filter((row) => {
            const s = searchText.toLowerCase();
            return (
                row.title.toLowerCase().includes(s)
            );
        });
    }, [notificationList]);

  return (
    <Box sx={{ p: '36px 24px 24px' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>{t('notifications.push_notifications.title', 'Push Notification Management')}</Typography>
            <Box display="flex" gap={2}>
            <TextField
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={t('search', 'Search')}
                sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    backgroundColor: 'rgba(230, 210, 249, 0.2)',
                    color: '#fff',
                    height: '38px',
                    '& fieldset': {
                        borderColor: 'rgba(230, 210, 249, 0.1)',
                    },
                    '&:hover fieldset': {
                        borderColor: 'rgba(230, 210, 249, 0.1)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'rgba(230, 210, 249, 0.1)',
                        borderWidth: '1px',
                    },
                },
                '& .MuiOutlinedInput-input': {
                    padding: '10px 16px',
                    color: '#FF69B4',
                    fontSize: '15px',
                    lineHeight: '18px',
                    
                },
                '& input::placeholder': {
                    color: 'rgba(255,255,255,0.6)',
                    opacity: 1,
                },
                '& .MuiFormHelperText-root': {
                    ml: '20px'
                }
                }}
            />
            {hasAction(MODULE_KEY, 'create') && (
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}
                    sx={{ 
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                        color: '#fff',
                        textTransform: 'none',
                        backgroundColor: '#FF69B4 !important',
                        borderRadius: '999px',
                        fontSize: '15px',
                        lineHeight: '24px',
                        padding: '0',
                        minWidth: '38px',
                        '& .MuiButton-startIcon': {
                            margin: 0,
                        },
                    }}
                ></Button>
            )}
            </Box>
        </Box>

        <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', px: '12px' }}>
            <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            slots={{ toolbar: GridToolbar }}
            paginationMode="server" 
            rowCount={pagination?.total || 0}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
                setPaginationModel(model);
            }}
            slotProps={{
                toolbar: {
                csvOptions: { disableToolbarButton: true }, // Removes the "Export as CSV" menu item
                printOptions: { disableToolbarButton: true }, // Removes the "Print" menu item
                },
            }}
            initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                sorting: { sortModel: [{ field: "createdAt", sort: "desc" }] }
            }}
            getRowId={(row) => row.id || row.sno}
             pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            localeText={i18n.language?.toLowerCase().startsWith('fr') ? frFR.components.MuiDataGrid.defaultProps.localeText : enUS.components.MuiDataGrid.defaultProps.localeText}
            sx={{ 
                backgroundColor: 'transparent', 
                border: 'none', 
                borderRadius: '0', 
                '& .MuiDataGrid-toolbarContainer': {
                padding: '12px',
                margin: '0 -12px',
                gap: '10px',
                backgroundColor: 'rgba(255,255,255,0.05)',
                },
                '& .MuiDataGrid-topContainer::after': {
                display: 'none',
                },
                '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
                color: '#fff',
                border: '1px solid rgba(230, 210, 249, 0.2)',
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 500,
                padding: '6px 12px',
                backgroundColor: 'rgba(255,255,255,0.05)',
                },
                '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-icon .MuiBadge-root svg': {
                width: '18px',
                height: '18px',
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                maxHeight: 'auto',
                height: 'auto !important'
                },
                '& .MuiDataGrid-virtualScroller': {
                borderRadius: '0 !important',
                },
                '& .MuiDataGrid-virtualScrollerRenderZone': {
                position: 'inherit',
                },
                '& .MuiDataGrid-virtualScrollerRenderZone>.MuiDataGrid-row': {
                maxHeight: 'none !important',
                height: 'auto !important',
                },
                '& .MuiDataGrid-cell': {
                height: 'auto !important',
                minHeight: 'var(--height)',
                paddingTop: '5px',
                paddingBottom: '5px',
                },
                '& .MuiDataGrid-cell>.MuiBox-root': {
                minHeight: '100%',
                alignItems: 'center',
                },
                '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
                borderRadius: '0 !important',
                borderColor: 'rgba(230, 210, 249, 0.2) !important',
                color: '#fff',
                },
                '& .MuiDataGrid-columnHeaders [role=row]': {
                backgroundColor: '#1a1a1a',
                },
                '& .MuiDataGrid-footerContainer': {
                borderColor: 'rgba(230, 210, 249, 0.2) !important'
                },
                '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
                color: 'white',
                },
                '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                outline: 'none',
                },
                '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                opacity: '0.8 !important',
                fill: 'white',
                },
                '& .MuiDataGrid-cell .MuiChip-outlined': {
                border: '0 !important',
                height: 'auto',
                fontWeight: '600',
                },
                '& .MuiDataGrid-cell .MuiChip-outlined.MuiChip-colorSuccess': {
                color: "#03c11c",
                },
                '& .MuiTablePagination-root': {
                color: '#fff'
                },
                '& .MuiTablePagination-selectLabel': {
                marginBottom: '0',
                },
                '& .MuiTablePagination-input': {
                marginRight: '10px',
                },
                '& .MuiTablePagination-input .MuiSelect-select': {
                paddingTop: '2px',
                paddingBottom: '2px',
                border: '1px solid rgba(230, 210, 249, 0.2)',
                backgroundColor: 'rgba(255,255,255,0.05)',
                borderRadius: '4px',
                },
                '& .MuiTablePagination-input .MuiSvgIcon-root': {
                fill: 'white'
                },
                '& .MuiTablePagination-toolbar .MuiTablePagination-displayedRows': {
                marginBottom: '0',
                },
                '& .MuiTablePagination-actions': {
                marginLeft: '10px !important',
                },
                '& .MuiTablePagination-actions .MuiButtonBase-root': {
                color: '#FF69B4',
                },
                '& .MuiDataGrid-filler': {
                height: 'auto !important',
                },
                '& .MuiDataGrid-filler>div': {
                borderColor: 'transparent',
                },
                '& .MuiDataGrid-overlayWrapper': {
                height: 'auto !important',
                maxHeight: '50px',
                },
                '& .MuiDataGrid-overlayWrapperInner': {
                minHeight: '40px',
                height: 'auto !important'
                },
                '& .MuiDataGrid-overlay': {
                color: '#fff',
                borderTop: '1px solid rgba(230, 210, 249, 0.2)',
                backgroundColor: 'transparent',
                minHeight: '40px',
                },
                // '& .MuiDataGrid-row .MuiDataGrid-cellOffsetLeft + .MuiDataGrid-cell + .MuiDataGrid-cell': {
                // display: 'flex',
                // alignItems: 'center',
                // justifyContent: 'center', 
                // },
            }}
            />
        </Box>

        <Dialog open={modalOpen} 
            onClose={(event, reason) => {
                if (submitting) return;

                if (reason !== "backdropClick") {
                    handleCloseModal();
                }
            }}
            fullWidth 
            sx={{ maxWidth: '810px !important', marginLeft: 'auto',   marginRight: 'auto', '& .MuiPaper-root': {
                maxWidth: '710px'
            } }}
        >
            <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
            { t('notifications.push_notifications.popup_titles.send_push_notification','Send Push Notification') }
            <IconButton onClick={handleCloseModal} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
                <Close />
            </IconButton>
            </DialogTitle>
            <Box 
                component="form"
                id="push-notification-form" 
                onSubmit={handleSubmit(onSubmit)}
                sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                }}
            >
                <DialogContent 
                    dividers 
                    sx={{ 
                        borderColor: 'rgba(230, 210, 249, 0.1)', 
                        padding: '24px 16px',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(230, 210, 249, 0.3)',
                            borderRadius: '4px',
                            '&:hover': {
                            background: 'rgba(230, 210, 249, 0.5)',
                            },
                        },
                    }}
                >
                
                    <Box sx={{ display: "flex", flexDirection: "column" }}> 
                    {/* Title */}
                    <Controller
                        name="title"
                        control={control}
                        rules={{
                            required: t(
                                'notifications.push_notifications.validations.title_required',
                                'Title is required'
                            ),
                            minLength: {
                                value: 3,
                                message: t(
                                'notifications.push_notifications.validations.title_min',
                                'Minimum 3 characters required'
                                ),
                            }
                        }}
                        render={({ field }) => (
                        <TextField
                            {...field}
                            error={!!errors.title}
                            helperText={errors.title?.message}
                            placeholder={t('notifications.push_notifications.fields.title','Title')}
                            sx={{ 
                            mb: 2,
                            '& .MuiOutlinedInput-input': {
                                padding: '12px 20px',
                                fontWeight: '400',
                                color: '#fff',
                            },
                            '& input::placeholder': {
                                color: 'rgba(255,255,255,0.6)',
                                opacity: 1,
                            },
                            }}
                        />
                        )}
                    />

                    {/* Message */}
                    <Controller
                        name="message"
                        control={control}
                        rules={{
                        required: t(
                            'notifications.push_notifications.validations.message_required',
                            'Message is required'
                        ),
                        minLength: {
                            value: 3,
                            message: t(
                            'notifications.push_notifications.validations.message_min',
                            'Minimum 3 characters required'
                            ),
                        }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                error={!!errors.message}
                                helperText={errors.message?.message}
                                multiline
                                rows={3}
                                placeholder={t('notifications.push_notifications.fields.message','Message')}
                                FormHelperTextProps={{
                                sx: {
                                    color: '#ffffff',
                                },
                                }}
                                sx={{ 
                                    mb: 2,
                                    '& .MuiInputBase-root': {
                                        padding: '0',
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px 20px 12px',
                                        fontWeight: '400',
                                        color: '#fff',
                                        margin: '10px 0',
                                        '&::-webkit-scrollbar': {
                                            width: '4px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: '4px',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: 'rgba(230, 210, 249, 0.3)',
                                            borderRadius: '4px',
                                        },
                                    },
                                    '& input::placeholder': {
                                        color: 'rgba(255,255,255,0.6)',
                                        opacity: 1,
                                    },
                                }}
                            />
                        )}
                    />

                        {/* Send To */}
                        <Controller
                            name="target_type"
                            control={control}
                            defaultValue=""
                            rules={{
                                required: t(
                                    'notifications.push_notifications.validations.target_type_required',
                                    'Please select target type'
                                ),
                            }}
                            render={({ field }) => (
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <Select
                                    {...field}
                                    value={field.value || ""}
                                    error={!!errors.target_type}
                                    onChange={(e) => field.onChange(e.target.value)}
                                    displayEmpty                                
                                    sx={{
                                        color: "#fff",
                                        fontWeight: 400,
                                        "& .MuiSelect-select": {
                                            padding: "12px 20px",
                                        },
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            border: "1px solid rgba(230, 210, 249, 0.1) !important",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            border: "1px solid rgba(230, 210, 249, 0.1)",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            border: "1px solid rgba(230, 210, 249, 0.1)",
                                        },
                                        "& .MuiSvgIcon-root": {
                                            color: "#fff",
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>{t('notifications.push_notifications.fields.send_to', "Send To")}</MenuItem>
                                    {USER_TYPES.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}                                    
                                </Select>
                                <FormHelperText
                                    sx={{
                                    color: '#d32f2f',
                                    marginLeft: '14px',
                                    }}
                                >
                                    {errors.target_type?.message}
                                </FormHelperText>
                            </FormControl>
                            )}
                        />

                        {/* users - Multiple Selection */}
                        { selectedTargetType === "SPECIFIC_USERS" && (
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="recipient_ids"
                                    control={control}
                                    defaultValue={[]}
                                    rules={{
                                        validate: (value) => {
                                        if (
                                            selectedTargetType === "SPECIFIC_USERS" &&
                                            (!value || value.length === 0)
                                        ) {
                                            return t(
                                            'notifications.push_notifications.validations.recipients_required',
                                            'Please select at least one recipient'
                                            );
                                        }

                                        return true;
                                        },
                                    }}
                                    render={({ field: { onChange, value, ...field } }) => (
                                        <Autocomplete
                                            {...field}
                                            multiple
                                            // open={open}
                                            // onOpen={() => setOpen(true)}
                                            // onClose={() => setOpen(false)}
                                            options={customers || []}
                                            getOptionLabel={(option) => option?.email || ""}
                                            value={customers.filter(option =>
                                                (value || []).includes(option.id)
                                            )}
                                            onChange={(event, newValue) => {
                                                onChange(newValue.map(item => item.id));
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={!!errors.recipient_ids}
                                                    helperText={errors.recipient_ids?.message}
                                                    placeholder={t('notifications.push_notifications.fields.select_recipients', "Select Recipients")}
                                                    sx={{
                                                        mb: 2,
                                                        '& .MuiOutlinedInput-root': {
                                                            padding: '4px 15px',
                                                            color: '#fff',
                                                        },
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            border: '1px solid rgba(230, 210, 249, 0.1) !important',
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            border: '1px solid rgba(230, 210, 249, 0.1)',
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            border: '1px solid rgba(230, 210, 249, 0.1)',
                                                        },
                                                        '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            )}
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        label={option?.email || ""}
                                                        {...getTagProps({ index })}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                                            color: '#fff',
                                                            border: '1px solid rgba(230, 210, 249, 0.1)',
                                                            '& .MuiChip-deleteIcon': {
                                                                color: '#fff',
                                                                '&:hover': {
                                                                    color: '#ff69b4',
                                                                },
                                                            },
                                                        }}
                                                    />
                                                ))
                                            }
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.id}>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {option.email}
                                                        </Typography>

                                                        {option.name && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ color: 'rgba(255,255,255,0.5)' }}
                                                            >
                                                                {option.name}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </li>
                                            )}
                                            ListboxProps={{
                                                sx: {
                                                    backgroundColor: '#252525',
                                                    color: '#fff',
                                                    '& .MuiAutocomplete-option': {
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(230, 210, 249, 0.1)',
                                                        },
                                                        '&[aria-selected="true"]': {
                                                            backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        )} 

                    {/* Action Type */}

                    <Controller
                        name="action_type"
                        control={control}
                        rules={{ 
                            required: t('notifications.push_notifications.validations.action_type_required', "Action type is required") 
                        }}
                        defaultValue=""
                        render={({ field }) => (
                            <FormControl fullWidth>
                                <Select
                                    {...field}
                                    value={field.value || ""}
                                    onChange={(e) => field.onChange(e.target.value)}
                                    error={!!errors.action_type}
                                    displayEmpty
                                    sx={{
                                    color: "#fff",
                                    fontWeight: 400,
                                    "& .MuiSelect-select": {
                                        padding: "12px 20px",
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        border: "1px solid rgba(230, 210, 249, 0.1) !important",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        border: "1px solid rgba(230, 210, 249, 0.1)",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        border: "1px solid rgba(230, 210, 249, 0.1)",
                                    },
                                    "& .MuiSvgIcon-root": {
                                        color: "#fff",
                                    },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                    {t('notifications.push_notifications.fields.action_type', "Action Type")}
                                    </MenuItem>
                                    {ACTION_TYPES.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText
                                    sx={{
                                        color: '#d32f2f',
                                        marginLeft: '14px',
                                    }}
                                    >
                                    {errors.action_type?.message}
                                </FormHelperText>
                            </FormControl>
                        )}
                        />
                    
                    {/* Screen Type */}
                    {selectActionType === 'OPEN_SCREEN' && (
                        <Controller
                            name="internal_screen"
                            control={control}
                            defaultValue=""
                            rules={{
                                required:
                                    selectActionType === "OPEN_SCREEN"
                                    ? t(
                                        'notifications.push_notifications.validations.internal_screen_required',
                                        'Please select screen name'
                                        )
                                    : false,
                            }}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <Select
                                        {...field}
                                        value={field.value || ""}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        error={!!errors.internal_screen}
                                        displayEmpty
                                        sx={{
                                        color: "#fff",
                                        fontWeight: 400,
                                        mt: 2,
                                        "& .MuiSelect-select": {
                                            padding: "12px 20px",
                                        },
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            border: "1px solid rgba(230, 210, 249, 0.1) !important",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            border: "1px solid rgba(230, 210, 249, 0.1)",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            border: "1px solid rgba(230, 210, 249, 0.1)",
                                        },
                                        "& .MuiSvgIcon-root": {
                                            color: "#fff",
                                        },
                                        }}
                                    >
                                        <MenuItem value="" disabled>{t('notifications.push_notifications.fields.screen', "Screen Name")}</MenuItem>
                                        {SCREENS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                                </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText
                                        sx={{
                                            color: '#ff6b6b',
                                            marginLeft: '14px',
                                        }}
                                    >
                                        {errors.internal_screen?.message}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px',flexWrap: 'wrap', gap: '6px' }}>
                    <Button 
                        onClick={handleCloseModal}
                        disabled={submitting}
                        sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}
                    >{t('cancel', 'Cancel')}</Button>
                    <Button 
                        variant="contained" 
                        type="submit"
                        onClick={() => { setActionType("send_now"); setValue("send_immediately", true); }}
                        disabled={submitting}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            textTransform: 'capitalize',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                        }}
                    >{submitting && actionType === "send_now" ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('notifications.push_notifications.actions.send_immediately', 'Send Immediately')}</Button>

                    <Button 
                        variant="contained" 
                        type="submit"
                        onClick={() => { setActionType("draft"); setValue("send_immediately", false); }}
                        disabled={submitting}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            textTransform: 'capitalize',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                        }}
                    >{submitting && actionType === "draft" ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('notifications.push_notifications.actions.save_as_draft', 'Save As Draft')}</Button>
                </DialogActions>
            </Box>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle sx={{ padding: '16px',fontSize: '18px',textAlign: 'center', }}>{t('notifications.push_notifications.popup_titles.delete', 'Delete Push Notification')}</DialogTitle>
                <DialogContent sx={{ pb: '0' }}>
                <Typography sx={{ fontSize: '15px',fontWeight: '400',textAlign: 'center', }}>{t('notifications.push_notifications.messages.confirm_delete', 'Are you sure you want to delete this Push Notification?')}</Typography>
                </DialogContent>
            <DialogActions
            sx={{
                px: '16px',
                py: '20px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '6px',
            }}
            >
            <Button 
                onClick={() => setDeleteDialogOpen(false)}
                sx={{
                backgroundColor: 'rgba(227, 214, 245, 0.2) !important',
                border: '1px solid rgba(227, 214, 245, 0.1)',
                fontSize: '15px',
                lineHeight: '20px',
                fontWeight: '400',
                padding: '11px 20px',
                minWidth: { xs: '100%', sm: '150px' },
                color: 'white',
                borderRadius: '999px',
                textTransform: 'capitalize',
                }}
            >{t('cancel', 'Cancel')}</Button>
            <Button 
                variant="contained" 
                disabled={deleting}
                onClick={handleDeleteConfirm}
                sx={{
                py: 1.5,
                borderRadius: '999px',
                fontWeight: '400',
                fontSize: '15px',
                lineHeight: '20px',
                background:
                    'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                textTransform: 'capitalize',
                minWidth: { xs: '100%', sm: '150px' },
                marginLeft: '10px',
                }}
            >
                {deleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('delete', 'Delete')}
            </Button>
            </DialogActions>
        </Dialog>

        <Dialog
            open={sendDialogOpen}
            onClose={(event, reason) => {
                if (sending) return;

                if (reason !== "backdropClick") {
                    setSendDialogOpen(false);
                }
            }}
        >
            <DialogTitle
                sx={{
                    padding: '16px',
                    fontSize: '18px',
                    textAlign: 'center',
                }}
            >
                {t(
                    'notifications.push_notifications.popup_titles.send_push_notification',
                    'Send Push Notification'
                )}
            </DialogTitle>
            <DialogContent sx={{ pb: '0' }}>
                <Typography
                    sx={{
                        fontSize: '15px',
                        fontWeight: '400',
                        textAlign: 'center',
                    }}
                >
                    {t(
                        'notifications.push_notifications.messages.confirm_send',
                        'Are you sure you want to send this push notification?'
                    )}
                </Typography>
            </DialogContent>
            <DialogActions
                sx={{
                    px: '16px',
                    py: '20px',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: '6px',
                }}
            >
                <Button
                    onClick={() => setSendDialogOpen(false)}
                    disabled={sending}
                    sx={{
                        backgroundColor:
                            'rgba(227, 214, 245, 0.2) !important',
                        border:
                            '1px solid rgba(227, 214, 245, 0.1)',
                        fontSize: '15px',
                        lineHeight: '20px',
                        fontWeight: '400',
                        padding: '11px 20px',
                        minWidth: { xs: '100%', sm: '150px' },
                        color: 'white',
                        borderRadius: '999px',
                        textTransform: 'capitalize',
                    }}
                >
                    {t('cancel', 'Cancel')}
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSendConfirm}
                    disabled={sending}
                    sx={{
                        py: 1.5,
                        borderRadius: '999px',
                        fontWeight: '400',
                        fontSize: '15px',
                        lineHeight: '20px',
                        background:
                            'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                        textTransform: 'capitalize',
                        minWidth: { xs: '100%', sm: '150px' },
                        marginLeft: '10px',
                    }}
                >
                    {sending ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('send', 'Send')}
                </Button>
            </DialogActions>
        </Dialog>

        {/* View Album Dialog */}
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
                {t('notifications.push_notifications.popup_titles.view',"View Push Notification")}
                <IconButton onClick={() => setViewDialogOpen(false)} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
                <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent 
                dividers 
                sx={{ 
                borderColor: 'rgba(230, 210, 249, 0.1)', 
                padding: '24px 16px',
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(230, 210, 249, 0.3)',
                    borderRadius: '4px',
                    '&:hover': {
                    background: 'rgba(230, 210, 249, 0.5)',
                    },
                },
                }}
            >
                {viewNotification ? (
                <>
                    <Grid container spacing={2}>
                        {/* Title */}
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '13px',
                                    mb: 0.5
                                }}
                            >
                                {t('notifications.push_notifications.fields.title', 'Title')}
                            </Typography>

                            <Typography
                                sx={{
                                    color: '#fff',
                                    fontSize: '16px',
                                    fontWeight: 600
                                }}
                            >
                                {viewNotification.title || '-'}
                            </Typography>
                        </Grid>

                        {/* Message */}
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '13px',
                                    mb: 0.5
                                }}
                            >
                                {t('notifications.push_notifications.fields.message', 'Message')}
                            </Typography>

                            <Typography
                                sx={{
                                    color: '#fff',
                                    fontSize: '15px',
                                    whiteSpace: 'pre-wrap'
                                }}
                            >
                                {viewNotification.message || '-'}
                            </Typography>
                        </Grid>

                        {/* Target Type */}
                        <Grid item xs={12} sm={12}>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '13px',
                                    mb: 0.5
                                }}
                            >
                                {t('notifications.push_notifications.fields.targetType', 'Target Type')}
                            </Typography>

                            <Typography sx={{ color: '#fff', fontSize: '15px' }}>
                                {
                                    USER_TYPES.find(
                                        (item) => item.value === viewNotification.targetType
                                    )?.label || "-"
                                }
                            </Typography>
                        </Grid>

                        {/* Action Type */}
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '13px',
                                    mb: 0.5
                                }}
                            >
                                {t('notifications.push_notifications.fields.actionType', 'Action Type')}
                            </Typography>

                            <Typography sx={{ color: '#fff', fontSize: '15px' }}>
                                {
                                    ACTION_TYPES.find(
                                        (item) => item.value === viewNotification.actionType
                                    )?.label || "-"
                                }
                            </Typography>
                        </Grid>

                        {/* Internal Screen */}
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '13px',
                                    mb: 0.5
                                }}
                            >
                                {t('notifications.push_notifications.fields.internalScreen', 'Internal Screen')}
                            </Typography>

                            <Typography sx={{ color: '#fff', fontSize: '15px' }}>
                                {viewNotification.internalScreen || '-'}
                            </Typography>
                        </Grid>

                        {/* Is Sent */}
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '13px',
                                    mb: 0.5
                                }}
                            >
                                {t('notifications.push_notifications.fields.isSent', 'Notification Sent')}
                            </Typography>

                            <Typography sx={{ color: '#fff', fontSize: '15px' }}>
                                {viewNotification.isSent ? 'Yes' : 'No'}
                            </Typography>
                        </Grid>

                        {/* Recipient IDs */}
                        {/* <Grid item xs={12}>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '13px',
                                    mb: 1
                                }}
                            >
                                {t('notifications.push_notifications.fields.recipientIds', 'Recipients')}
                            </Typography>

                            {viewNotification.recipientIds?.length > 0 ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1
                                    }}
                                >
                                    {viewNotification.recipientIds.map((id) => (
                                        <Chip
                                            key={id}
                                            label={id}
                                            size="small"
                                            sx={{
                                                backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                                color: '#fff',
                                                border: '1px solid rgba(230, 210, 249, 0.1)',
                                            }}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography sx={{ color: '#fff', fontSize: '15px' }}>
                                    All Users
                                </Typography>
                            )}
                        </Grid> */}

                        {/* Sent At */}
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '13px',
                                    mb: 0.5
                                }}
                            >
                                {t('notifications.push_notifications.fields.sentAt', 'Sent At')}
                            </Typography>

                            <Typography sx={{ color: '#fff', fontSize: '15px' }}>
                                {
                                    viewNotification.sentAt
                                        ? formatUTCDate(viewNotification.sentAt)
                                        : '-'
                                }
                            </Typography>
                        </Grid>

                        {/* Created At */}
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '13px',
                                    mb: 0.5
                                }}
                            >
                                {t('notifications.push_notifications.fields.createdAt', 'Created At')}
                            </Typography>

                            <Typography sx={{ color: '#fff', fontSize: '15px' }}>
                                {
                                    viewNotification.createdAt
                                        ? formatUTCDateForDataGrid(viewNotification.createdAt)
                                        : '-'
                                }
                            </Typography>
                        </Grid>

                            {/* <Typography variant="h6" gutterBottom sx={{ fontSize: '18px', mb: '10px' }}>
                                {viewNotification.title}
                            </Typography>

                            <Typography variant="body2" gutterBottom sx={{ fontSize: '14px', mb: '5px' }}>
                            {t('notifications.push_notifications.fields.message',"Message")}:{" "}
                                {( viewNotification.message || '' )}
                            </Typography>

                            <Typography variant="body2" gutterBottom sx={{ fontSize: '14px', mb: '5px' }}>
                            {t('notifications.push_notifications.fields.actionType',"Action Type")}:{" "}
                                {
                                    ACTION_TYPES.find(
                                        (item) => item.value === viewNotification.actionType
                                    )?.label || "-"
                                }
                            </Typography>

                            <Typography variant="body2" gutterBottom sx={{ fontSize: '14px', mb: '5px' }}>
                            {t('notifications.push_notifications.fields.createdAt',"Created At")}: { formatUTCDateForDataGrid(viewNotification.createdAt) }
                            </Typography>                     */}
                    </Grid>
                </>
                ) : (
                <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: '16px', py: '20px',flexWrap: 'wrap', gap: '6px' }}>
                <Button onClick={() => setViewDialogOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>{t('close','Close')}</Button>
            </DialogActions>
        </Dialog>

        <GlobalStyles
            styles={{
            '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
                backgroundColor: '#252525 !important',
                border: '1px solid rgba(230, 210, 249, 0.1)',
                fontSize: '15px',
                color: '#fff',
                borderRadius: '4px',
            },
            '.MuiDialog-root .MuiPaper-root': {
                borderRadius: '20px',
            },
            '.MuiDataGrid-columnsManagementHeader': {
                padding: '10px !important',
            },
            '.MuiInputBase-formControl': {
                fontSize: '14px !important',
                color: 'white !important',
                borderRadius: '999px !important',
            },
            '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
                border: '1px solid rgba(230, 210, 249, 0.1) !important',
            },
            '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
                fill: 'white',
            },
            '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
                borderTop: '1px solid rgba(230, 210, 249, 0.1)',
                padding: '4px 8px 4px 16px',
            },
            '.MuiDataGrid-paper .MuiDataGrid-columnsManagement':{
                padding: '0px 16px 12px',
            },
            '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
                fontSize: '14px',
            },
            '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
                fill: 'white',
            },
            '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
                color: '#FF69B4',
            },
            '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                fill: 'white',
            },
            '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
                color: 'white',
            },
            '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
                borderColor: 'rgba(230, 210, 249, 0.1) !important',
            },
            '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
                display: 'none',
            },
            '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
                padding: '0',
            },
            '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
                fontSize: '14px',
            },
            '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
                padding: '5px 10px',
            },
            '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
                fill: '#FF69B4',
            },
            '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
                borderColor: 'rgba(230, 210, 249, 0.3) !important',
                margin: '0',
            },
            '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                minWidth: '32px',
            },
            }}
        />
    </Box>
  );
}

export default PushNotificationManagement;


